import React from 'react'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import styled from 'styled-components'
import { History, Location } from 'history'

// Utils
import { breakpoint } from '@sketch/global-styles'

// Icons
import { ReactComponent as LockIcon } from '@sketch/icons/lock-64-svg'

// Constants
import { RouteParams, routes } from '@sketch/constants'

// Components
import PageLayout from 'layouts/PageLayout'
import EmptyHeader from 'containers/EmptyHeader'
import {
  Box,
  Flex,
  Text,
  Button,
  LinkButton,
  ErrorMessage,
} from '@sketch/components'
import { IsSignedIn, IsNotSignedIn } from 'containers/IsSignedIn'
import { useToast } from '@sketch/toasts'
import {
  HasNoPersonalIdentity,
  HasPersonalIdentity,
} from 'containers/HasPersonalIdentity'

// Hooks
import useSignOut from 'modules/user/useSignOut'
import useUserSignedIn from 'modules/user/useUserSignedIn'
import useUserProfile from 'modules/user/useUserProfile'

// Types
import { useShareRequestAccessMutation } from '@sketch/gql-types'

// Styles
const StyledErrorMessage = styled(ErrorMessage)`
  flex: 1 0 auto;
  padding: 0 16px;
`

const StyledLinkButton = styled(LinkButton).attrs({ size: '40' })`
  &:last-child {
    ${breakpoint('base', 'xs')`
      margin-left: 0px;
    `}
  }
`

const Buttons = styled(Flex)`
  flex-direction: column;

  a {
    margin-bottom: 8px;
  }

  button {
    margin: 0px;
  }

  ${breakpoint('xs')`
    flex-direction: row;

    a {
      margin-bottom: 0px;
    }

    button {
      margin-left: 24px;
    }
  `};
`

const NoAccessActions = ({
  location,
  onSignOutClick,
}: {
  location: Location
  onSignOutClick: () => void
}) => (
  <Buttons>
    <LinkButton
      to={{
        pathname: routes.ENTRY.create({}),
        state: { from: location },
      }}
    >
      Close
    </LinkButton>
    <Button variant="primary" onClick={onSignOutClick}>
      Sign Out
    </Button>
  </Buttons>
)

const RequestShareAccessActions = ({
  location,
  history,
  shareShortId,
}: {
  location: Location
  history: History
  shareShortId: string
}) => {
  const { showToast } = useToast()

  const signOut = useSignOut({
    location: {
      pathname: routes.SIGN_IN.create({}),
      state: {
        from: location,
      },
    },
    reason: 'Switching account to access share',
  })

  const [requestShareAccess, { loading }] = useShareRequestAccessMutation({
    redirectErrors: true,
    variables: { shortId: shareShortId },
    onError: ({ message }) => {
      showToast(message, 'negative')
      /* Redirect home of the user already requested access before */
      if (message === "You've already requested access") {
        history.push(routes.ENTRY.create({}))
      }
    },
    onCompleted: () => {
      showToast('Request has been sent')
      /* Redirect home instead of showing the ShareAuthorizer NotFound */
      history.push(routes.ENTRY.create({}))
    },
  })

  return (
    <Buttons>
      <Button onClick={() => signOut()}>Switch Account</Button>
      <Button
        variant="primary"
        onClick={() => requestShareAccess()}
        loading={loading}
      >
        Request Access
      </Button>
    </Buttons>
  )
}

const SignUpOrSignInView = ({ location }: { location: Location }) => (
  <Buttons>
    <StyledLinkButton
      to={{ pathname: routes.SIGN_IN.create({}), state: { from: location } }}
    >
      Sign In
    </StyledLinkButton>
    <StyledLinkButton
      variant="primary"
      to={{ pathname: routes.SIGN_UP.create({}), state: { from: location } }}
    >
      Sign Up
    </StyledLinkButton>
  </Buttons>
)

const ErrorDescription = ({
  isUserSignedIn,
  hasPersonalIdentity,
}: {
  isUserSignedIn: boolean
  hasPersonalIdentity: boolean
}) => {
  if (isUserSignedIn && !hasPersonalIdentity) {
    return (
      <Box maxWidth={640} mb={5}>
        <Text textStyle="copy.quaternary.standard.E">
          You cannot request access to this document using your Workspace&apos;s
          SSO account.
          <br />
          To request access, sign in with your email and password — or sign out
          and create an account.
        </Text>
      </Box>
    )
  } else {
    return <>You don’t have permission from the owner to view this document.</>
  }
}

interface RequiresMembershipViewProps
  extends RouteComponentProps<RouteParams<'SHARE_VIEW'>> {}

const RequiresMembershipView = ({
  location,
  history,
  match,
}: RequiresMembershipViewProps) => {
  const signOut = useSignOut({
    reason: 'Requires membership',
  })
  const isUserSignedIn = useUserSignedIn()
  const { data } = useUserProfile()

  const hasPersonalIdentity = data?.me?.hasPersonalIdentity ?? false

  return (
    <PageLayout header={<EmptyHeader />}>
      <StyledErrorMessage
        icon={<LockIcon />}
        iconSize="medium"
        title="The document you’re trying to access is private"
        extra={
          <>
            <IsSignedIn>
              <HasPersonalIdentity>
                <RequestShareAccessActions
                  location={location}
                  history={history}
                  shareShortId={match?.params?.shareID}
                />
              </HasPersonalIdentity>
              <HasNoPersonalIdentity>
                <NoAccessActions location={location} onSignOutClick={signOut} />
              </HasNoPersonalIdentity>
            </IsSignedIn>
            <IsNotSignedIn>
              <SignUpOrSignInView location={location} />
            </IsNotSignedIn>
          </>
        }
      >
        <ErrorDescription
          isUserSignedIn={isUserSignedIn}
          hasPersonalIdentity={hasPersonalIdentity}
        />
      </StyledErrorMessage>
    </PageLayout>
  )
}

export default withRouter(RequiresMembershipView)
